import { useState, type FormEvent } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { buildApiUrl } from "../lib/api-client";

export default function LoginPage(): JSX.Element {
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const { refreshSession } = useAuth();

    // Validate and sanitize redirect parameter to prevent malformed URLs
    const redirectParam = searchParams.get("redirect") || "/";
    const redirect = redirectParam.startsWith("/") ? redirectParam : "/";

    const handleSubmit = async (e: FormEvent) => {
        e.preventDefault();
        setError(null);
        setIsLoading(true);

        try {
            const response = await fetch(buildApiUrl("/auth/login"), {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                credentials: "include",
                body: JSON.stringify({ email, password }),
            });

            if (!response.ok) {
                const data = await response.json();
                throw new Error(data?.error || "Sign in failed");
            }

            await refreshSession();
            navigate(redirect);
        } catch (err) {
            setError(err instanceof Error ? err.message : "An error occurred");
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="auth-container">
            <div className="auth-card animate-slide-up">
                <div className="auth-header">
                    <div className="logo-container">
                        <svg
                            className="logo-icon"
                            width="48"
                            height="48"
                            viewBox="0 0 48 48"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                        >
                            <rect
                                x="8"
                                y="8"
                                width="32"
                                height="32"
                                rx="8"
                                fill="url(#gradient)"
                            />
                            <path
                                d="M16 20h16M16 24h12M16 28h16"
                                stroke="white"
                                strokeWidth="2"
                                strokeLinecap="round"
                            />
                            <defs>
                                <linearGradient
                                    id="gradient"
                                    x1="8"
                                    y1="8"
                                    x2="40"
                                    y2="40"
                                    gradientUnits="userSpaceOnUse"
                                >
                                    <stop stopColor="var(--color-primary)" />
                                    <stop offset="1" stopColor="var(--color-accent)" />
                                </linearGradient>
                            </defs>
                        </svg>
                    </div>
                    <h1>Welcome Back</h1>
                    <p className="auth-subtitle">Sign in to your account to continue</p>
                </div>

                <form onSubmit={handleSubmit} className="auth-form">
                    {error && (
                        <div className="auth-error animate-fade-in">
                            <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                                <path d="M8 0a8 8 0 100 16A8 8 0 008 0zm1 12H7v-2h2v2zm0-3H7V4h2v5z" />
                            </svg>
                            {error}
                        </div>
                    )}

                    <div className="form-group">
                        <label htmlFor="email">Email Address</label>
                        <input
                            id="email"
                            name="email"
                            type="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            placeholder="you@example.com"
                            required
                            autoComplete="username"
                            disabled={isLoading}
                        />
                    </div>

                    <div className="form-group">
                        <label htmlFor="password">
                            Password
                            <Link to="/forgot-password" className="forgot-link">
                                Forgot?
                            </Link>
                        </label>
                        <input
                            id="password"
                            name="password"
                            type="password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            placeholder="••••••••"
                            required
                            autoComplete="current-password"
                            disabled={isLoading}
                        />
                    </div>

                    <button
                        type="submit"
                        className="button primary auth-submit"
                        disabled={isLoading}
                    >
                        {isLoading ? (
                            <>
                                <span className="spinner" />
                                Signing in...
                            </>
                        ) : (
                            "Sign In"
                        )}
                    </button>
                </form>

                <div className="auth-footer">
                    <p>
                        Don't have an account?{" "}
                        <Link to="/register" className="auth-link">
                            Create one
                        </Link>
                    </p>
                </div>
            </div>

            <style>{`
        .auth-container {
          min-height: 100vh;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 2rem 1rem;
          background: var(--color-bg);
        }

        .auth-card {
          width: 100%;
          max-width: 420px;
          background: var(--color-surface);
          backdrop-filter: blur(12px) saturate(180%);
          border-radius: var(--radius-base);
          border: 1px solid var(--color-border);
          box-shadow: var(--shadow-lg);
          padding: 2.5rem;
        }

        .auth-header {
          text-align: center;
          margin-bottom: 2rem;
        }

        .logo-container {
          display: inline-flex;
          margin-bottom: 1.5rem;
        }

        .logo-icon {
          filter: drop-shadow(0 4px 12px hsla(220, 90%, 56%, 0.3));
        }

        .auth-header h1 {
          font-size: 1.75rem;
          margin-bottom: 0.5rem;
          color: var(--color-text);
        }

        .auth-subtitle {
          color: var(--color-text-muted);
          font-size: 0.9375rem;
        }

        .auth-form {
          display: flex;
          flex-direction: column;
          gap: 1.25rem;
        }

        .auth-error {
          display: flex;
          align-items: center;
          gap: 0.625rem;
          padding: 0.875rem 1rem;
          background: hsla(0, 72%, 51%, 0.1);
          border: 1px solid hsla(0, 72%, 51%, 0.3);
          border-radius: calc(var(--radius-base) - 2px);
          color: var(--color-critical);
          font-size: 0.875rem;
        }

        .form-group {
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
        }

        .form-group label {
          font-size: 0.875rem;
          font-weight: 500;
          color: var(--color-text);
          display: flex;
          align-items: center;
          justify-content: space-between;
        }

        .forgot-link {
          color: var(--color-primary);
          font-weight: 400;
          transition: color 0.2s;
        }

        .forgot-link:hover {
          color: var(--color-primary-hover);
          text-decoration: underline;
        }

        .form-group input {
          padding: 0.75rem 1rem;
          border: 1px solid var(--color-border);
          border-radius: calc(var(--radius-base) - 2px);
          font-size: 1rem;
          background: var(--color-surface);
          color: var(--color-text);
          transition: all 0.2s ease;
        }

        .form-group input:focus {
          outline: none;
          border-color: var(--color-primary);
          box-shadow: 0 0 0 3px var(--color-primary-soft);
        }

        .form-group input:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .form-group input::placeholder {
          color: var(--color-text-muted);
        }

        .auth-submit {
          margin-top: 0.5rem;
          width: 100%;
          padding: 0.875rem;
          font-size: 1rem;
        }

        .spinner {
          display: inline-block;
          width: 16px;
          height: 16px;
          border: 2px solid rgba(255, 255, 255, 0.3);
          border-top-color: white;
          border-radius: 50%;
          animation: spin 0.6s linear infinite;
        }

        @keyframes spin {
          to { transform: rotate(360deg); }
        }

        .auth-footer {
          margin-top: 2rem;
          padding-top: 2rem;
          border-top: 1px solid var(--color-border);
          text-align: center;
        }

        .auth-footer p {
          font-size: 0.9375rem;
          color: var(--color-text-muted);
        }

        .auth-link {
          color: var(--color-primary);
          font-weight: 500;
          transition: color 0.2s;
        }

        .auth-link:hover {
          color: var(--color-primary-hover);
          text-decoration: underline;
        }

        @media (max-width: 640px) {
          .auth-card {
            padding: 2rem 1.5rem;
          }

          .auth-header h1 {
            font-size: 1.5rem;
          }
        }
      `}</style>
        </div>
    );
}
